import React, { useState, useEffect, useMemo } from "react";
import { useManufacturer } from "../../contexts/WithManufacturer";
import LegacyReseller from '../../stores/reseller/Reseller';
import { getUserSelectedOpportunity, setUserSelectedOpportunity } from '../../utils/LocalStorageUtils';
import { useGetV4ManufacturerByRouteManufacturerIdOpportunitiesQuery, usePutV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdMutation, Reseller as ApiReseller, OpportunityEntry } from "randmar-api-client";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, useToast } from "randmarcomps";
import { Loader2 } from "lucide-react";

interface IProps {
  reseller: LegacyReseller | ApiReseller;
  children: React.ReactNode;
}

const isLegacyReseller = (r: LegacyReseller | ApiReseller): r is LegacyReseller => 'resellerId' in r && !('ResellerId' in r);

const AddResellerToOpportunityWrapper: React.FC<IProps> = ({ reseller: rawReseller, children }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = useState(false);
  const [chosenOpportunity, setChosenOpportunity] = useState(getUserSelectedOpportunity() || "");
  const { toast } = useToast();

  const { data: opportunitiesData, isLoading: isLoadingOpportunities } = useGetV4ManufacturerByRouteManufacturerIdOpportunitiesQuery({ routeManufacturerId: applicationId });
  const opportunities = Array.isArray(opportunitiesData) ? (opportunitiesData as OpportunityEntry[]) : [];
  const [addReseller, { isLoading: isAdding }] = usePutV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerIdMutation();

  const reseller = useMemo<ApiReseller>(() => {
    if (isLegacyReseller(rawReseller)) {
      return {
        ResellerId: rawReseller.resellerId ? String(rawReseller.resellerId) : undefined,
        Name: rawReseller.name,
      };
    }
    return rawReseller;
  }, [rawReseller]);

  useEffect(() => {
    if (!chosenOpportunity && opportunities && opportunities.length > 0) {
      setChosenOpportunity(opportunities[0].BidNumber || "");
    }
  }, [opportunities, chosenOpportunity]);

  const handleCancel = () => {
    setOpen(false);
  };

  const propertyChange = (newValue: string) => {
    setChosenOpportunity(newValue);
    setUserSelectedOpportunity(newValue || "");
  };

  const handleConfirmResellerOpportunityRequest = async () => {
    if (!reseller.ResellerId) return;

    try {
      await addReseller({
        routeManufacturerId: applicationId,
        bidNumber: chosenOpportunity,
        resellerId: reseller.ResellerId
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `${reseller.Name} added to opportunity ${chosenOpportunity}` });
      setOpen(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: `Could not add reseller to opportunity` });
    }
  };

  const isLoading = isLoadingOpportunities || isAdding;

  return (
    <>
      <div onClick={() => setOpen(true)}>
        {children}
      </div>
      <Dialog
        open={open}
        onOpenChange={(isOpen) => { if (!isLoading && !isOpen) handleCancel(); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center">
              Add {reseller.Name} to Opportunity
            </DialogTitle>
          </DialogHeader>
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              <div className="py-4">
                <Select
                  value={chosenOpportunity}
                  onValueChange={propertyChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an opportunity" />
                  </SelectTrigger>
                  <SelectContent>
                    {opportunities.map(opportunity => <SelectItem key={"Opportunity" + opportunity.BidNumber} value={opportunity.BidNumber || ""}><b>{opportunity.BidNumber}</b> | {opportunity.Opportunity}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="flex justify-between">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmResellerOpportunityRequest}
                  disabled={chosenOpportunity === ""}>
                  Confirm
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddResellerToOpportunityWrapper;
